<script setup>
/**
 * "Not set up yet" and "Ask us to finish setup" (ease package P-5.5).
 *
 * A workspace whose broadcast sending was never set up gets the sidecar's 409 on every
 * broadcast read. That is a STATE: nothing is broken and no retry changes it, and the only
 * one who can set it up is us. So it gets one sentence and one button, and the button asks
 * us (one request a day; a second press is answered the same way, because to her the
 * answer is the same: we have it). Modelled on `components/outbound/OutboundNotSetUp.vue`.
 */
import { ref } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';

import { useWasloI18n } from '../../i18n.js';
import { wasloApi } from '../../api/client.js';

const props = defineProps({
  accountId: { type: Number, default: null },
});

const { t } = useWasloI18n();

/** `idle` → `pending` → `sent`, or back to a live button on `error`. */
const state = ref('idle');

const ask = async () => {
  if (
    props.accountId === null ||
    state.value === 'pending' ||
    state.value === 'sent'
  ) {
    return;
  }
  state.value = 'pending';
  try {
    await wasloApi.requestSetup(props.accountId, 'broadcasts');
    state.value = 'sent';
  } catch {
    state.value = 'error';
  }
};
</script>

<template>
  <div
    class="flex flex-col items-center justify-center gap-3 py-16 text-center"
    data-waslo-broadcasts-not-set-up
  >
    <span
      class="size-6 i-lucide-megaphone text-n-slate-10"
      aria-hidden="true"
    />
    <span class="text-base font-medium text-n-slate-12">
      {{ t('WASLO.BROADCASTS.NOT_SET_UP.TITLE') }}
    </span>
    <p class="max-w-md mb-0 text-sm leading-6 text-n-slate-11">
      {{ t('WASLO.BROADCASTS.NOT_SET_UP.BODY') }}
    </p>
    <div class="flex flex-col items-center gap-2" aria-live="polite">
      <p
        v-if="state === 'sent'"
        class="mb-0 text-sm text-n-teal-11"
        data-waslo-broadcasts-asked
      >
        {{ t('WASLO.BROADCASTS.NOT_SET_UP.ASKED') }}
      </p>
      <Button
        v-else
        :label="
          state === 'pending'
            ? t('WASLO.BROADCASTS.NOT_SET_UP.ASKING')
            : t('WASLO.BROADCASTS.NOT_SET_UP.ASK')
        "
        size="sm"
        :is-loading="state === 'pending'"
        :disabled="accountId === null"
        data-waslo-broadcasts-ask
        @click="ask"
      />
      <p
        v-if="state === 'error'"
        class="mb-0 text-sm text-n-ruby-11"
        data-waslo-broadcasts-ask-error
      >
        {{ t('WASLO.BROADCASTS.NOT_SET_UP.FAILED') }}
      </p>
    </div>
  </div>
</template>
